import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Button } from 'tailwind-v4/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from 'tailwind-v4/components/field'
import { Input } from 'tailwind-v4/components/input'
import {
  NativeSelect,
  NativeSelectOption,
} from 'tailwind-v4/components/native-select'
import useGlobal from '@/hooks/useGlobal'
import tankApi from '@/state/tank/tank.slice'
import { brand } from '@/pages/[org_id]/account/organisation/brand/layout'
import { useVenues } from '@/hooks/useVenue'
import { useEmailAddresses } from '@/pages/[org_id]/marketing/email/domains/hooks'
import GoogleAddressSelect from '@/common/GoogleAddressSelect/GoogleAddressSelect'

export const CREATE_REVIEW_PAGE_EVENT = 'stampede:create-review-page'

/**
 * Open the global create-review-page dialog from anywhere (e.g. the nav's
 * "Add Review Page" action) without navigating — it renders over the current
 * page rather than routing to `/reviews`.
 */
export function openCreateReviewPage() {
  window.dispatchEvent(new Event(CREATE_REVIEW_PAGE_EVENT))
}

const EMPTY_PAGE = {
  title_text: '',
  serial: '',
  branding_id: '',
  google_page_id: '',
  sender_from_id: '',
}

// The form (and its queries) only mount while the dialog is open, so nothing
// is fetched on every page just because the dialog is globally mounted.
function CreateReviewPageForm({ onClose }: { onClose: () => void }) {
  const { orgId } = useGlobal()
  const navigate = useNavigate()
  const [venues] = useVenues()
  const [newPage, setNewPage] = useState(EMPTY_PAGE)

  const { data: emailAddresses } = useEmailAddresses()
  const { data: brandKitsData } = brand.useGetBrandsQuery(
    { orgId, cursor: '', limit: 100 },
    { skip: !orgId }
  )
  const brandKits = brandKitsData?.data ?? []

  const { data: reviewPages = [] } = tankApi.useGetOrgReviewPagesQuery(
    { orgId },
    { skip: !orgId }
  )

  const usedSerials = useMemo(
    () => reviewPages.map((page: any) => page.serial).filter(Boolean),
    [reviewPages]
  )
  const availableVenues = useMemo(
    () => venues.filter((v: any) => !usedSerials.includes(v.serial)),
    [venues, usedSerials]
  )

  const [createPage, { isLoading: isCreating }] =
    tankApi.useCreateReviewsPageMutation()

  const canCreate =
    !isCreating &&
    Boolean(newPage.title_text) &&
    Boolean(newPage.serial) &&
    Boolean(newPage.branding_id) &&
    Boolean(newPage.google_page_id) &&
    Boolean(newPage.sender_from_id)

  const handleCreatePage = async () => {
    if (!canCreate) return
    const venueName =
      venues.find((v: any) => v.serial === newPage.serial)?.alias || 'us'

    try {
      const res = await createPage({
        orgId,
        page: {
          title_text: newPage.title_text,
          serial: newPage.serial,
          branding_id: newPage.branding_id,
          google_page_id: newPage.google_page_id,
          google_enabled: true,
          is_active: 1,
          org_id: orgId,
          subject: `How was your visit to ${venueName}?`,
          body_text: `<p>We hope you enjoyed your visit to ${venueName}. We'd love to hear your feedback — it only takes a moment and helps us keep improving.</p>`,
          send_after_secs: 60 * 60,
          facebook_page_id: null,
          tripadvisor_url: null,
          custom_prompt_id: null,
          custom_copy_id: null,
          happy_or_not: false,
          send_on_visit: true,
          sender_from_id: newPage.sender_from_id,
          sender_from_type: 'sender',
          send_external_for_rating: 4,
        },
      }).unwrap()

      onClose()

      if (res.serial) {
        navigate(`/${orgId}/venues/${res.serial}/reviews/settings`)
      } else {
        navigate(`/${orgId}/venues/unlinked/reviews/settings?page_id=${res.id}`)
      }
    } catch (error) {
      toast.error('Failed to create review page')
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create review page</DialogTitle>
        <DialogDescription>
          Set up a review page for a venue — guests are asked for feedback and
          routed to Google.
        </DialogDescription>
      </DialogHeader>

      <FieldGroup className="-mx-4 min-h-0 flex-1 gap-5 overflow-y-auto px-4 py-1 scroll-py-2">
        <Field>
          <FieldLabel htmlFor="create-venue">Venue</FieldLabel>
          <NativeSelect
            id="create-venue"
            className="w-full"
            value={newPage.serial}
            onChange={(e) => {
              const value = e.target.value
              const venue = venues.find((v: any) => v.serial === value)
              const venueName = venue?.alias || venue?.serial || ''
              setNewPage((p) => ({
                ...p,
                serial: value,
                title_text: value ? `How did ${venueName} do?` : '',
              }))
            }}
          >
            <NativeSelectOption value="">Select venue</NativeSelectOption>
            {venues.map((v: any) => (
              <NativeSelectOption
                key={v.serial}
                value={v.serial}
                disabled={usedSerials.includes(v.serial)}
              >
                {v.alias || v.serial}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {availableVenues.length === 0 && venues.length > 0 ? (
            <FieldDescription>
              All venues already have review pages.
            </FieldDescription>
          ) : null}
        </Field>

        <Field>
          <FieldLabel htmlFor="create-title">Page title</FieldLabel>
          <Input
            id="create-title"
            value={newPage.title_text}
            onChange={(e) => {
              setNewPage((p) => ({ ...p, title_text: e.target.value }))
            }}
            placeholder={
              newPage.serial
                ? `How did ${venues.find((v: any) => v.serial === newPage.serial)?.alias || 'your venue'} do?`
                : 'Select a venue first'
            }
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="create-brand">Brand kit</FieldLabel>
          <NativeSelect
            id="create-brand"
            className="w-full"
            value={newPage.branding_id}
            onChange={(e) => {
              setNewPage((p) => ({ ...p, branding_id: e.target.value }))
            }}
          >
            <NativeSelectOption value="">Select brand kit</NativeSelectOption>
            {brandKits.map((kit: any) => (
              <NativeSelectOption key={kit.id} value={kit.id}>
                {kit.name || 'Default'}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>

        <Field>
          <FieldLabel htmlFor="create-sender">Send from</FieldLabel>
          <NativeSelect
            id="create-sender"
            className="w-full"
            value={newPage.sender_from_id}
            onChange={(e) => {
              setNewPage((p) => ({ ...p, sender_from_id: e.target.value }))
            }}
          >
            <NativeSelectOption value="">Select sender</NativeSelectOption>
            {(emailAddresses?.data ?? []).map((addr: any) => (
              <NativeSelectOption key={addr.id} value={addr.id}>
                {addr.name} &lt;{addr.email}&gt;
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>

        <Field>
          <FieldLabel>Google location</FieldLabel>
          <GoogleAddressSelect
            placeholder="Search for your venue on Google"
            onAddressSelect={(geo_settings: any) => {
              const placeId = geo_settings.googlePlaceId
              if (placeId) {
                setNewPage((p) => ({ ...p, google_page_id: placeId }))
                toast.success('Google Place ID linked')
              } else {
                toast.error('No Google Place ID found for this address')
              }
            }}
            inputClassName="border-border bg-transparent"
          />
          {newPage.google_page_id ? (
            <FieldDescription className="text-success">
              Google Place ID linked
            </FieldDescription>
          ) : null}
        </Field>
      </FieldGroup>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleCreatePage}
          disabled={!canCreate}
        >
          {isCreating ? 'Creating…' : 'Create page'}
        </Button>
      </DialogFooter>
    </>
  )
}

/**
 * Globally-mounted (org layout) create-review-page dialog. Opened via
 * `openCreateReviewPage()` so the "Add Review Page" nav action opens it over
 * the current page instead of routing to `/reviews`.
 */
export function CreateReviewPageDialog() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handler = () => {
      setOpen(true)
    }
    window.addEventListener(CREATE_REVIEW_PAGE_EVENT, handler)
    return () => {
      window.removeEventListener(CREATE_REVIEW_PAGE_EVENT, handler)
    }
  }, [])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="new-ui-theme flex max-h-[85dvh] flex-col gap-4 sm:max-w-lg">
        {/* Mount the form (and its queries) only while open, so nothing is
            fetched just because the dialog is globally mounted. */}
        {open ? (
          <CreateReviewPageForm
            onClose={() => {
              setOpen(false)
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
